package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.mutableStateSetOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.Modal
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.AvatarTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Checkbox
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.membersSettings.MembersSettingsContract
import co.maytes.standupbot.dashboard.features.membersSettings.MembersSettingsContract.InviteTab
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.slack.SlackDirectoryUserDto
import co.maytes.standupbot.shared.dto.workspace.InviteDeliveryDto
import co.maytes.standupbot.shared.dto.workspace.InviteLinkDto
import co.maytes.standupbot.shared.policy.MemberPermissions
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Clock
import org.jetbrains.compose.web.attributes.InputType

private const val SLACK_TAB_LABEL = "Slack teammate"
private const val EMAIL_TAB_LABEL = "Email"
private const val LINK_TAB_LABEL = "Invite link"
private const val EMAIL_COPY =
    "StandupBot signs in with Slack. Copy this link and send it to them yourself."

@Composable
fun InviteMemberModal(
    state: MembersSettingsContract.State,
    actorRole: RoleDto,
    dispatch: (MembersSettingsContract.Intent) -> Unit,
    onCopy: (String) -> Unit,
) {
    val grantable = MemberPermissions.grantableRoles(actorRole)
    val defaultRole = if (RoleDto.MEMBER in grantable) RoleDto.MEMBER else grantable.firstOrNull() ?: RoleDto.VIEWER
    var role by remember { mutableStateOf(defaultRole) }
    var email by remember { mutableStateOf("") }
    var linkExpiryDays by remember { mutableStateOf(7) }
    var linkMaxUses by remember { mutableStateOf("") }
    var linkLabel by remember { mutableStateOf("") }
    val selection = remember { mutableStateSetOf<String>() }

    val emailValid = email.contains('@') && email.contains('.')
    val maxUsesValue = linkMaxUses.trim().toIntOrNull()
    val maxUsesValid = linkMaxUses.isBlank() || (maxUsesValue != null && maxUsesValue > 0)
    val finished = state.inviteOutcomes.isNotEmpty()
    val close = { dispatch(MembersSettingsContract.Intent.CloseInvite) }

    Modal(
        onDismiss = close,
        title = "Invite teammates",
        widthPx = 620,
        submitting = state.inviteSubmitting,
        footer = {
            Button(label = if (finished) "Done" else "Cancel", variant = ButtonVariant.Ghost, onClick = close)
            when {
                finished -> Unit
                state.inviteTab == InviteTab.SLACK ->
                    Button(
                        label = if (state.inviteSubmitting) "Sending..." else sendLabel(selection.size),
                        variant = ButtonVariant.Primary,
                        enabled = selection.isNotEmpty() && grantable.isNotEmpty() && !state.inviteSubmitting,
                        onClick = { dispatch(MembersSettingsContract.Intent.SendSlackInvites(selection.toList(), role)) },
                    )
                state.inviteTab == InviteTab.EMAIL ->
                    Button(
                        label = if (state.inviteSubmitting) "Creating..." else "Create link",
                        variant = ButtonVariant.Primary,
                        enabled = emailValid && grantable.isNotEmpty() && !state.inviteSubmitting,
                        onClick = { dispatch(MembersSettingsContract.Intent.SendEmailInvite(email, role)) },
                    )
                else ->
                    Button(
                        label = if (state.inviteSubmitting) "Creating..." else "Create link",
                        variant = ButtonVariant.Primary,
                        enabled = maxUsesValid && grantable.isNotEmpty() && !state.inviteSubmitting,
                        onClick = {
                            dispatch(
                                MembersSettingsContract.Intent.CreateInviteLink(
                                    role = role,
                                    expiresInDays = linkExpiryDays,
                                    maxUses = maxUsesValue,
                                    label = linkLabel.trim().ifEmpty { null },
                                ),
                            )
                        },
                    )
            }
        },
    ) {
        Column(modifier = Modifier.gap(Spacing.md)) {
            InviteTabs(active = state.inviteTab, onSelect = { dispatch(MembersSettingsContract.Intent.SetInviteTab(it)) })

            if (grantable.isEmpty()) {
                MutedText("Your role cannot invite people.")
            } else if (finished) {
                OutcomeList(state.inviteOutcomes, onCopy)
            } else {
                when (state.inviteTab) {
                    InviteTab.SLACK ->
                        SlackTab(
                            state = state,
                            selection = selection,
                            role = role,
                            grantable = grantable,
                            onRole = { role = it },
                            onReload = { dispatch(MembersSettingsContract.Intent.ReloadDirectory) },
                        )
                    InviteTab.EMAIL ->
                        EmailTab(email = email, onEmail = { email = it }, role = role, grantable = grantable, onRole = { role = it })
                    InviteTab.LINK ->
                        LinkTab(
                            state = state,
                            role = role,
                            grantable = grantable,
                            onRole = { role = it },
                            expiryDays = linkExpiryDays,
                            onExpiry = { linkExpiryDays = it },
                            maxUses = linkMaxUses,
                            onMaxUses = { linkMaxUses = it },
                            maxUsesValid = maxUsesValid,
                            label = linkLabel,
                            onLabel = { linkLabel = it },
                            onCopy = onCopy,
                            onRevoke = { dispatch(MembersSettingsContract.Intent.RevokeInviteLink(it)) },
                        )
                }
            }

            state.inviteError?.let { msg ->
                SpanText(
                    text = msg,
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.status(StatusHue.Danger).solid)
                        .margin(top = Spacing.xxs),
                )
            }
        }
    }
}

private fun sendLabel(count: Int): String =
    when (count) {
        0 -> "Send DMs"
        1 -> "Send 1 DM"
        else -> "Send $count DMs"
    }

@Composable
private fun MutedText(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .color(Palette.textSecondary),
    )
}

@Composable
private fun InviteTabs(
    active: InviteTab,
    onSelect: (InviteTab) -> Unit,
) {
    Row(modifier = Modifier.gap(Spacing.xs)) {
        listOf(
            InviteTab.SLACK to SLACK_TAB_LABEL,
            InviteTab.EMAIL to EMAIL_TAB_LABEL,
            InviteTab.LINK to LINK_TAB_LABEL,
        ).forEach { (tab, label) ->
            Button(
                label = label,
                variant = if (tab == active) ButtonVariant.Secondary else ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                onClick = { onSelect(tab) },
            )
        }
    }
}

@Composable
private fun RoleSelect(
    role: RoleDto,
    grantable: List<RoleDto>,
    onRole: (RoleDto) -> Unit,
) {
    Select(
        value = role,
        options = grantable.map { SelectOption(it, MembersCopy.roleLabel(it)) },
        onValueChange = onRole,
        label = "Role",
        help = "You can grant up to ${MembersCopy.roleLabel(grantable.first())}",
    )
}

@Composable
private fun SlackTab(
    state: MembersSettingsContract.State,
    selection: MutableSet<String>,
    role: RoleDto,
    grantable: List<RoleDto>,
    onRole: (RoleDto) -> Unit,
    onReload: () -> Unit,
) {
    var search by remember { mutableStateOf("") }
    val q = search.trim().lowercase()
    val members = state.memberSlackIds
    val invited = state.invitedSlackIds
    val visible =
        state.directory
            .filter { user ->
                q.isEmpty() ||
                    user.displayName.lowercase().contains(q) ||
                    user.realName?.lowercase()?.contains(q) == true ||
                    user.email?.lowercase()?.contains(q) == true
            }
            .sortedBy { it.slackUserId in members || it.slackUserId in invited }

    TextInput(
        value = search,
        onValueChange = { search = it },
        placeholder = "Search workspace members...",
        ariaLabel = "Search workspace members",
    )
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(Spacing.xs)
            .styleModifier {
                property("overflow-y", "auto")
                property("min-height", "180px")
                property("max-height", "300px")
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                property("border-radius", "${Radius.md}")
            },
    ) {
        when {
            state.directoryLoading -> MutedText("Loading Slack workspace...")
            state.directoryFailed ->
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(Spacing.sm).padding(Spacing.sm)) {
                    MutedText("Slack didn't answer.")
                    Button(label = "Try again", variant = ButtonVariant.Ghost, size = ButtonSize.Sm, onClick = onReload)
                }
            visible.isEmpty() -> MutedText(if (q.isEmpty()) "No Slack users available." else "No matches for \"$search\".")
            else ->
                visible.forEach { user ->
                    val blocked = user.slackUserId in members || user.slackUserId in invited
                    DirectoryRow(
                        user = user,
                        checked = user.slackUserId in selection,
                        note = when {
                            user.slackUserId in members -> "already on StandupBot"
                            user.slackUserId in invited -> "already invited"
                            else -> "not on StandupBot yet"
                        },
                        enabled = !blocked,
                        onToggle = {
                            if (user.slackUserId in selection) selection.remove(user.slackUserId) else selection.add(user.slackUserId)
                        },
                    )
                }
        }
    }
    RoleSelect(role, grantable, onRole)
    MutedText("We'll DM them a link that expires in 7 days.")
}

@Composable
private fun DirectoryRow(
    user: SlackDirectoryUserDto,
    checked: Boolean,
    note: String,
    enabled: Boolean,
    onToggle: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.inner)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .onClick { if (enabled) onToggle() }
            .styleModifier {
                property("user-select", "none")
                if (!enabled) property("opacity", "0.5")
                if (checked) property("background-color", Palette.accentSubtle.cssRgb())
            },
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = { if (enabled) onToggle() },
            disabled = !enabled,
            ariaLabel = user.displayName,
        )
        Avatar(name = user.displayName, src = user.avatarUrl, sizePx = 28, tone = AvatarTone.Accent)
        Column(modifier = Modifier.styleModifier { property("flex", "1 1 auto") }) {
            SpanText(
                text = user.displayName + if (user.isGuest) " (guest)" else "",
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(500)
                    .color(Palette.textPrimary),
            )
            SpanText(
                text = note,
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary),
            )
        }
    }
}

@Composable
private fun EmailTab(
    email: String,
    onEmail: (String) -> Unit,
    role: RoleDto,
    grantable: List<RoleDto>,
    onRole: (RoleDto) -> Unit,
) {
    MutedText(EMAIL_COPY)
    TextInput(
        value = email,
        onValueChange = onEmail,
        label = "Email",
        type = InputType.Email,
        placeholder = "teammate@company.com",
    )
    RoleSelect(role, grantable, onRole)
}

@Composable
private fun OutcomeList(
    outcomes: List<MembersSettingsContract.InviteOutcome>,
    onCopy: (String) -> Unit,
) {
    Column(modifier = Modifier.gap(Spacing.sm).fillMaxWidth()) {
        outcomes.forEach { outcome ->
            val failed = outcome.error != null || outcome.delivery == InviteDeliveryDto.DM_FAILED
            Row(
                modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    text = outcome.label,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(500)
                        .color(Palette.textPrimary)
                        .styleModifier { property("flex", "1 1 auto") },
                )
                SpanText(
                    text = outcomeText(outcome),
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(if (failed) Palette.status(StatusHue.Danger).solid else Palette.textSecondary),
                )
                outcome.acceptUrl?.takeIf { outcome.delivery != InviteDeliveryDto.DM_SENT }?.let { url ->
                    Button(label = "Copy link", variant = ButtonVariant.Secondary, size = ButtonSize.Sm, onClick = { onCopy(url) })
                }
            }
        }
    }
}

private fun outcomeText(outcome: MembersSettingsContract.InviteOutcome): String =
    when {
        outcome.error != null -> outcome.error
        outcome.delivery == InviteDeliveryDto.DM_SENT -> "DM sent"
        outcome.delivery == InviteDeliveryDto.DM_FAILED -> "DM failed: copy link"
        else -> "Link only: copy and send it yourself"
    }

@Composable
@Suppress("LongParameterList")
private fun LinkTab(
    state: MembersSettingsContract.State,
    role: RoleDto,
    grantable: List<RoleDto>,
    onRole: (RoleDto) -> Unit,
    expiryDays: Int,
    onExpiry: (Int) -> Unit,
    maxUses: String,
    onMaxUses: (String) -> Unit,
    maxUsesValid: Boolean,
    label: String,
    onLabel: (String) -> Unit,
    onCopy: (String) -> Unit,
    onRevoke: (String) -> Unit,
) {
    MutedText("Anyone with this link can join with the role you choose after signing in with Slack.")
    RoleSelect(role, grantable, onRole)
    Select(
        value = expiryDays,
        options = listOf(SelectOption(1, "1 day"), SelectOption(7, "7 days"), SelectOption(30, "30 days")),
        onValueChange = onExpiry,
        label = "Expires after",
    )
    TextInput(
        value = maxUses,
        onValueChange = onMaxUses,
        label = "Max uses (optional)",
        placeholder = "Unlimited",
        error = if (maxUsesValid) null else "Enter a whole number above zero",
    )
    TextInput(value = label, onValueChange = onLabel, label = "Label (optional)", placeholder = "Backend team onboarding")
    SpanText(
        text = "Active links",
        modifier = Modifier
            .fontSize(Typography.label.size)
            .fontWeight(600)
            .color(Palette.textPrimary)
            .margin(top = Spacing.sm),
    )
    when {
        state.inviteLinksLoading -> MutedText("Loading links...")
        state.inviteLinks.isEmpty() -> MutedText("No active links yet.")
        else ->
            state.inviteLinks.forEach { link ->
                InviteLinkRow(link = link, onCopy = onCopy, onRevoke = { onRevoke(link.id) })
            }
    }
}

@Composable
private fun InviteLinkRow(
    link: InviteLinkDto,
    onCopy: (String) -> Unit,
    onRevoke: () -> Unit,
) {
    val now = remember(link) { Clock.System.now() }
    Row(
        modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.styleModifier { property("flex", "1 1 auto") }) {
            SpanText(
                text = MembersCopy.inviteLinkTitle(link) + " · ${MembersCopy.roleLabel(link.role)}",
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(500)
                    .color(Palette.textPrimary),
            )
            SpanText(
                text = MembersCopy.inviteLinkSummary(link, now),
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary),
            )
        }
        Button(
            label = "Copy",
            variant = ButtonVariant.Secondary,
            size = ButtonSize.Sm,
            enabled = link.url != null,
            onClick = { link.url?.let(onCopy) },
        )
        Button(label = "Revoke", variant = ButtonVariant.Ghost, size = ButtonSize.Sm, onClick = onRevoke)
    }
}
